import React from 'react';
import Box from '@mui/material/Box';
import Divider from "@mui/material/Divider";
import { useParams } from 'react-router-dom';

import { AttemptRateChart } from 'components/Analytics/ScoreDistribution';
import { WhiteBox } from 'components/AppShell';
import { getLeaderboard, getAverageAttemptRateDetails } from 'services/analyticsV2';
import { getTemplateById } from 'services';
import QuestionsAnalytics from 'components/TemplatePerformance/QuestionsAnalytics';
import UsersAnalytics from 'components/TemplatePerformance/UsersAnalytics';
import TemplateInfo from 'components/TemplatePerformance/TemplateInfo';
import ShadowBox from 'components/ShadowBox';
import ScreenHeading from 'components/ScreenHeading';
import LeaderBoard from 'components/Analytics/LeaderBoard';
import ScoreDistributionChart  from 'components/Analytics/ScoreDistribution';

function TemplatePerformance(props) {
  const { id } = useParams();

  const [template, setTemplate] = React.useState({});
  const [interviewDetails, setInterviewDetails] = React.useState({});
  const [interviewDetailsLoading, setInterviewDetailsLoading] = React.useState(false);
  const [interviewDetailsError, setInterviewDetailsError] = React.useState(null);
  const [attemptRateData, setAttemptRateData] = React.useState(null);
  const [attemptRateLoading, setAttemptRateLoading] = React.useState(true);
  const [attemptRateError, setAttemptRateError] = React.useState(null);

  const [leaderboardData, setLeaderboardData] = React.useState({ topPerformers: [], bestPerformers: [] });
  const [leaderboardLoading, setLeaderboardLoading] = React.useState(true);
  const [leaderboardError, setLeaderboardError] = React.useState(null);
  const [leaderboardCategory, setLeaderboardCategory] = React.useState('best_score');

  React.useEffect(() => {
    getTemplateById(id, { domain: true, batch: true })
      .then(template => setTemplate(template))
      .catch(console.error)
  }, [id]);

  React.useEffect(() => {
    if (!id) return;
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    
    const params = { type: 'interview', id, interviewVariantType: 'assessment' };
    params.leaderboardType = leaderboardCategory;
    
    getLeaderboard(params)
      .then(response => setLeaderboardData(response || { topPerformers: [], bestPerformers: [] }))
      .catch(err => setLeaderboardError(err.message))
      .finally(() => setLeaderboardLoading(false));
  }, [id, leaderboardCategory]);

  React.useEffect(() => {
    if (!id) return;
    setAttemptRateLoading(true);
    setAttemptRateError(null);
    getAverageAttemptRateDetails({ type: 'interview', id, interviewVariantType: 'assessment' })
      .then(response => setAttemptRateData(response))
      .catch(err => setAttemptRateError(err.message))
      .finally(() => setAttemptRateLoading(false));
  }, [id]);

  return (
    <Box display="flex" flexDirection="column" minHeight='calc(100vh - 76px)' gap={4}>
      <ScreenHeading title="Assessment Analytics" id="assessment-analytics"></ScreenHeading>
      <TemplateInfo 
        interviewDetails={interviewDetails}
        loading={interviewDetailsLoading}
        error={interviewDetailsError}
        interviewType='Assignment'
        template={template}
      />
      <Box display='flex' gap={4}>
        <WhiteBox sx={{width : '100% !important'}}>
          <LeaderBoard 
            data={leaderboardData} 
            loading={leaderboardLoading} 
            error={leaderboardError}
            leaderboardCategory={leaderboardCategory}
            setLeaderboardCategory={setLeaderboardCategory}
            interviewVariantType="assessment"
          />
        </WhiteBox>
        <Box display='flex' width='100%' flexDirection='column' gap={4}>
          <WhiteBox>
            <ScoreDistributionChart
              avgDistribution={{
                batchAverageScore: Math.round(attemptRateData?.averageScoreStats?.averageScore || 0)
              }}
              scoreDistribution={attemptRateData?.averageScoreStats || {"0-30": 0, "31-60": 0, "61-100": 0}}
              totalStudents={attemptRateData?.totalStudents || 0}
              interviewVariantType="assessment"
            />
          </WhiteBox>
          <WhiteBox>
            <AttemptRateChart  
                attemptRate={{
                  attemptRate: attemptRateData?.attemptRateStats?.attemptRate || 0,
                  userSubmittedCount: (attemptRateData?.attemptRateStats?.["31-60"] || 0) + (attemptRateData?.attemptRateStats?.["61-100"] || 0),
                  userNotSubmittedCount: attemptRateData?.attemptRateStats?.["0-30"] || 0
                }}
                loading={attemptRateLoading}
                error={attemptRateError}
                totalStudents={attemptRateData?.totalStudents || 0}
            />
          </WhiteBox>
        </Box>
      </Box>
     
      <ShadowBox sx={{ mt: 0 }}>
        {
          (template.type !== 'follow_up') ?
            <>
              <QuestionsAnalytics template={template} />
              <br />
              <Divider light flexItem />
            </> :
            null
        }
        <UsersAnalytics
          batch={template?.batch?._id}
          template={template}
          forTemplateAnalytics={true}
          interviewType="assessment"
        />
      </ShadowBox>
    </Box>
  );
}

export default TemplatePerformance;